import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { Section, Overline, SectionTitle } from "@/components/Primitives";
import { Leaf, Sparkles } from "lucide-react";

export default function Menu() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ categories: [], items: [] });
  const [activeCat, setActiveCat] = useState("all");
  const lang = i18n.language;
  const tx = (obj) => obj?.[lang] || obj?.fr || "";

  useEffect(() => {
    api.get("/menu").then(r => setData(r.data));
  }, []);

  const filtered = useMemo(() => {
    if (activeCat === "all") return data.items;
    return data.items.filter(i => i.category === activeCat);
  }, [activeCat, data.items]);

  const cats = data.categories;

  return (
    <div data-testid="menu-page" className="pt-32">
      <Section testId="menu-header">
        <Overline>{t("menu_page.overline")}</Overline>
        <h1 className="font-serif-luxe text-5xl sm:text-6xl font-light tracking-tight text-[#F8F5EF] mt-3 mb-6">{t("menu_page.title")}</h1>
        <p className="max-w-3xl text-[#F8F5EF]/70 leading-relaxed">{t("menu_page.intro")}</p>

        {/* Filters */}
        <div className="mt-12">
          <div className="flex flex-wrap gap-2 pb-2">
            <button
              data-testid="menu-filter-all"
              onClick={() => setActiveCat("all")}
              className={`px-5 py-2.5 text-[10px] tracking-[0.22em] uppercase border transition-all whitespace-nowrap ${activeCat === "all" ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 text-[#F8F5EF]/70 hover:border-[#D4AF37]/50"}`}
            >
              {t("menu_page.filter_all")}
            </button>
            {cats.map(c => (
              <button
                key={c.key}
                data-testid={`menu-filter-${c.key}`}
                onClick={() => setActiveCat(c.key)}
                className={`px-5 py-2.5 text-[10px] tracking-[0.22em] uppercase border transition-all whitespace-nowrap ${activeCat === c.key ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 text-[#F8F5EF]/70 hover:border-[#D4AF37]/50"}`}
              >
                {tx(c.name)}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section testId="menu-list" className="!pt-0">
        {cats
          .filter(c => activeCat === "all" || c.key === activeCat)
          .map(cat => {
            const items = filtered.filter(it => it.category === cat.key);
            if (!items.length) return null;
            return (
              <div key={cat.key} className="mb-20" data-testid={`menu-section-${cat.key}`}>
                <div className="flex items-baseline gap-6 mb-10">
                  <h2 className="font-serif-luxe text-3xl sm:text-4xl font-light text-[#D4AF37] tracking-tight">{tx(cat.name)}</h2>
                  <div className="flex-1 h-px bg-[#D4AF37]/20" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-14 gap-y-8">
                  {items.map(it => (
                    <div key={it.id} className="group flex items-baseline gap-4" data-testid={`menu-item-${it.id}`}>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <h3 className="font-serif-luxe text-xl text-[#F8F5EF]">{tx(it.name)}</h3>
                          {(it.tags || []).includes("signature") && (
                            <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]"><Sparkles size={10} /> {t("menu_page.tag_signature")}</span>
                          )}
                          {(it.tags || []).includes("vegetarian") && (
                            <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase text-emerald-400/80"><Leaf size={10} /> {t("menu_page.tag_vegetarian")}</span>
                          )}
                        </div>
                        {tx(it.description) && (
                          <p className="mt-1 text-sm text-[#F8F5EF]/60 leading-relaxed">{tx(it.description)}</p>
                        )}
                      </div>
                      <div className="font-serif-luxe text-xl text-[#D4AF37] whitespace-nowrap">{it.price.toFixed(2)} €</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        <div className="mt-12 pt-8 border-t border-[#D4AF37]/15">
          <p className="text-xs text-[#F8F5EF]/50 italic">{t("menu_page.alcohol_warning")}</p>
        </div>
      </Section>
    </div>
  );
}
